import { SmartFilter } from '@Portal/models/smartFilters/SmartFilter';
import { useAxios } from '@Shared/composables/useAxios';
import { usePortalUri } from '@Shared/composables/usePortalUri';
import { DEFAULT_REPORTING_DATASOURCE, DEFAULT_SOCIAL_DATASOURCE } from '@Shared/constants';
import { useRootStore } from '@Shared/stores/root';
import { DashboardArguments } from '@Shared/types/portal/DashboardArguments';
import { UserDisplayInfo } from '@Shared/types/portal/PortalFeatures';
import {
  ProcessflowActionHistoryResponse,
  ProcessflowActionResponse,
  ProcessFlowCard,
  ProcessFlowObjectDetail,
  ProcessFlowQueue,
  ProcessFlowQueueCount,
} from '@Shared/types/portal/ProcessFlow';
import { RawData } from '@Shared/types/portal/RawData';
import { SmartDataSelectionViewModel } from '@Shared/types/portal/SmartDataSelectionViewModel';
import {
  CustomerCentreStageDataSource,
  DashboardType,
  FrontlineDashboardDataSource,
  ReportingDataSource,
  BenchmarkDashboardDataSource,
  SocialReviewsDashboardDataSource,
  SrgCustomerBoardDataSource,
} from '@Shared/types/portal/dashboard';
import { BaseVisualResult, FilterResult } from '@Shared/utils/types';

const axios = useAxios();

export async function getDashboardDataSource(
  moduleId: string
): Promise<
  | FrontlineDashboardDataSource
  | SrgCustomerBoardDataSource
  | SocialReviewsDashboardDataSource
  | ReportingDataSource
  | BenchmarkDashboardDataSource
  | null
> {
  const uri = usePortalUri();
  const resp = (
    await axios.get<
      | FrontlineDashboardDataSource
      | SrgCustomerBoardDataSource
      | SocialReviewsDashboardDataSource
      | ReportingDataSource
      | BenchmarkDashboardDataSource
    >(`${uri}/api/dashboard/datasource/${moduleId}`)
  ).data;
  // backwards compatibility fix since "type" isn't on older portal builds
  if (moduleId.toLowerCase().includes('FrontlineDashboard'.toLowerCase())) {
    (resp as FrontlineDashboardDataSource).type = DashboardType.Frontline;
  } else if (moduleId.toLowerCase().includes('SrgCustomerBoard'.toLowerCase())) {
    (resp as SrgCustomerBoardDataSource).type = DashboardType.SrgCustomerBoard;
  } else if (moduleId.toLowerCase().includes('BenchmarkDashboard'.toLowerCase())) {
    (resp as BenchmarkDashboardDataSource).type = DashboardType.Benchmarking;
  } else if (resp.isSocial === true) {
    resp.type = DashboardType.Social;
    resp.visualTypes = DEFAULT_SOCIAL_DATASOURCE.visualTypes;
  } else if (moduleId.toLowerCase().includes('Reporting'.toLowerCase())) {
    resp.type = DashboardType.Reporting;
    resp.visualTypes = DEFAULT_REPORTING_DATASOURCE.visualTypes;
  }
  return resp;
}

export async function getCustomerCentreStageDataSource(
  moduleId: string
): Promise<CustomerCentreStageDataSource | null> {
  const uri = usePortalUri();
  const resp = await axios.get<CustomerCentreStageDataSource | null>(`${uri}/api/dashboard/datasource/${moduleId}`);
  return resp.data;
}

export async function getVisualData(
  moduleId: string,
  visualKey: string,
  filterOverrides?: SmartFilter[],
  args?: {
    // the page to load
    page?: number;
    // tab to load for (used for social reviews feed)
    feedTab?: string;
    // sort order (used for social reviews feed)
    sortOrder?: string;
  }
): Promise<BaseVisualResult & any> {
  const store = useRootStore();
  if (store.jsonDemoData?.data) return store.jsonDemoData.data[visualKey];

  const uri = usePortalUri();
  const { data } = await axios.post<BaseVisualResult>(
    `${uri}/api/dashboard/visuals/${moduleId}/${visualKey}`,
    JSON.stringify({
      smartFilters: filterOverrides,
      ...args,
    } satisfies DashboardArguments)
  );
  return data;
}

export async function getFilterData(
  moduleId: string,
  visualKey: string,
  smartFilters?: SmartFilter[]
): Promise<FilterResult> {
  const store = useRootStore();
  if (store.jsonDemoData?.filters) return { filters: store.jsonDemoData.filters[visualKey] as any };

  const uri = usePortalUri();
  const resp = await axios.post<FilterResult>(
    `${uri}/api/dashboard/filters/${moduleId}/${visualKey}`,
    JSON.stringify({
      smartFilters,
    } satisfies DashboardArguments)
  );
  return resp.data;
}

export async function getBenchmarkFiltersData(
  moduleId: string,
  visualKey: string,
  smartFilters?: SmartFilter[],
  locationFilterKey?: string
): Promise<any> {
  const store = useRootStore();
  if (store.jsonDemoData?.filters) return { filters: store.jsonDemoData.filters[visualKey] as any };

  const uri = usePortalUri();
  const resp = await axios.post<FilterResult>(
    `${uri}/api/dashboard/filters/${moduleId}/${visualKey}`,
    JSON.stringify({
      smartFilters,
      locationFilterKey,
    } satisfies DashboardArguments)
  );
  return resp.data;
}

export async function getDateFilterData(moduleId: string): Promise<Record<string, string>> {
  const uri = usePortalUri();
  const resp = await axios.get<Record<string, string>>(`${uri}/api/dashboard/visuals/dateFilters/${moduleId}`);
  return resp.data;
}

export async function getProcessFlowObjectHistory(
  moduleId: string,
  objectId: number
): Promise<ProcessflowActionHistoryResponse> {
  const uri = usePortalUri();
  const response = await axios.get<ProcessflowActionHistoryResponse>(
    `${uri}/api/processflow/${moduleId}/processflowObjectHistory/${objectId}`
  );
  return response.data;
}

export async function getProcessFlowQueueCounts(moduleId: string): Promise<ProcessFlowQueueCount[]> {
  const uri = usePortalUri();
  const response = await axios.get<ProcessFlowQueueCount[]>(`${uri}/api/processflow/${moduleId}/queuecounts`);
  return response.data;
}

export async function getValidMentionableUsers(moduleId?: string): Promise<UserDisplayInfo[]> {
  const uri = usePortalUri();
  const url = moduleId
    ? `${uri}/api/processflow/validMentionableUsers?moduleId=${encodeURIComponent(moduleId)}`
    : `${uri}/api/processflow/validMentionableUsers`;
  const response = await axios.get<UserDisplayInfo[]>(url);
  return response.data;
}

export async function getValidUserAllocations(): Promise<UserDisplayInfo[]> {
  const uri = usePortalUri();
  const response = await axios.get<UserDisplayInfo[]>(`${uri}/api/processflow/validUserAllocations`);
  const unsorted = Array.isArray(response.data) ? response.data : [];
  return unsorted.sort((a, b) => {
    const nameA = a.userFriendlyName.toUpperCase();
    const nameB = b.userFriendlyName.toUpperCase();
    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
  });
}

export async function performAction(
  moduleId: string,
  objectId: string,
  actionModel: {
    actionName: string;
    actionProperties: string;
  }
): Promise<ProcessflowActionResponse> {
  const uri = usePortalUri();
  const response = await axios.post<ProcessflowActionResponse>(
    `${uri}/api/processflow/${moduleId}/processFlowObject/action/${objectId}`,
    JSON.stringify(actionModel)
  );
  return response.data;
}

export async function getDataForProcessFlowObject(
  moduleId: string,
  sourceObjectId: string
): Promise<ProcessFlowObjectDetail> {
  const uri = usePortalUri();
  const response = await axios.get<ProcessFlowObjectDetail>(
    `${uri}/api/processflow/${moduleId}/processFlowObject/${sourceObjectId}/PF`
  );
  return response.data;
}

export async function getRawValue(moduleId: string, cleanedFieldName: string, recordId: string): Promise<string> {
  const uri = usePortalUri();
  const response = await axios.get<RawData>(`${uri}/api/smartdata/rawData/${moduleId}`, {
    params: {
      cleanedFieldName,
      recordId,
    },
  });
  const rawData = response.data;
  return rawData.rawValue;
}

export async function getQueues(moduleId: string): Promise<ProcessFlowQueue[]> {
  const uri = usePortalUri();
  const response = await axios.get<ProcessFlowQueue[]>(`${uri}/api/processflow/${moduleId}/queues`);
  return response.data;
}

export async function getProcessFlowTemplateObjects(
  moduleId: string,
  queueName: string,
  smartDataSelectionViewModel: SmartDataSelectionViewModel
): Promise<{ data: ProcessFlowCard[]; recordCount: number }> {
  const uri = usePortalUri();
  const resp = await axios.post<{ data: ProcessFlowCard[]; recordCount: number }>(
    `${uri}/api/processflow/${moduleId}/queue/${queueName}/PF/templateObjects`,
    JSON.stringify(smartDataSelectionViewModel)
  );
  return resp.data;
}
